import { useState } from 'react';
import {
  CircleAlertIcon,
  ExternalLinkIcon,
  GlobeIcon,
  LoaderCircleIcon,
  ListChecksIcon,
  SearchIcon,
  SparklesIcon,
} from 'lucide-react';
import type { WebResearchActivityDTO } from '@shared/schemas/web-tools';
import { cn } from '@/lib/utils';
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtSearchResult,
  ChainOfThoughtSearchResults,
  ChainOfThoughtStep,
} from '@/components/ai-elements/chain-of-thought';
import type { ToolPart } from '@/components/ai-elements/tool';

export type WebActivityToolPart = {
  errorText?: string;
  input?: unknown;
  output?: unknown;
  state: ToolPart['state'];
  type: string;
  toolName?: string;
};

type WebActivityStep = {
  detail: string | null;
  domains: string[];
  isError: boolean;
  isPending: boolean;
  key: string;
  label: string;
  type: 'fetch' | 'planning' | 'search' | 'synthesizing';
};

function toRecord(value: unknown) {
  return value && typeof value === 'object' ? value as Record<string, unknown> : null;
}

function getRawToolName(part: WebActivityToolPart) {
  return part.type === 'dynamic-tool'
    ? part.toolName ?? ''
    : part.type.replace(/^tool-/, '');
}

function getHostname(value: unknown) {
  if (typeof value !== 'string') {
    return null;
  }

  try {
    return new URL(value).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

function getResultDomains(results: unknown[]) {
  return Array.from(new Set(results
    .map(toRecord)
    .map((result) => getHostname(result?.url))
    .filter((hostname): hostname is string => Boolean(hostname))))
    .slice(0, 5);
}

function isPendingState(state: ToolPart['state']) {
  return state === 'input-streaming' || state === 'input-available' || state === 'approval-requested';
}

function isErrorState(state: ToolPart['state']) {
  return state === 'output-error' || state === 'output-denied';
}

function toWebActivityStep(part: WebActivityToolPart, index: number): WebActivityStep | null {
  const toolName = getRawToolName(part);
  const input = toRecord(part.input);
  const output = toRecord(part.output);
  const isError = Boolean(part.errorText) || isErrorState(part.state) || output?.error === true;
  const isPending = isPendingState(part.state);

  if (toolName === 'web_search') {
    const query = typeof input?.query === 'string' ? input.query.trim() : '';
    const results = Array.isArray(output?.results) ? output.results : [];

    return {
      detail: isError
        ? 'La recherche n’a pas abouti'
        : results.length > 0
          ? `${results.length} résultat${results.length > 1 ? 's' : ''} trouvé${results.length > 1 ? 's' : ''}`
          : null,
      domains: getResultDomains(results),
      isError,
      isPending,
      key: `${toolName}-${index}`,
      label: query ? `Recherche de « ${query} »` : 'Recherche sur le web',
      type: 'search',
    };
  }

  if (toolName === 'fetch_url') {
    const outputTitle = typeof output?.title === 'string' ? output.title.trim() : '';
    const hostname = getHostname(output?.finalUrl ?? output?.url ?? input?.url);

    return {
      detail: isError ? 'La page n’a pas pu être consultée' : hostname,
      domains: [],
      isError,
      isPending,
      key: `${toolName}-${index}`,
      label: outputTitle || (hostname ? `Consultation de ${hostname}` : 'Consultation d’une page'),
      type: 'fetch',
    };
  }

  return null;
}

function toTypedActivitySteps(activities: WebResearchActivityDTO[]): WebActivityStep[] {
  const latestByPhase = new Map<WebResearchActivityDTO['phase'], WebResearchActivityDTO>();
  const steps: WebActivityStep[] = [];
  for (const activity of activities) {
    latestByPhase.set(activity.phase, activity);
  }

  for (const phase of ['planning', 'searching', 'reading', 'synthesizing'] as const) {
    const activity = latestByPhase.get(phase);
    if (!activity) {
      continue;
    }

    if (activity.phase === 'planning') {
      steps.push({
        detail: null,
        domains: [],
        isError: false,
        isPending: activity.status === 'running',
        key: activity.phase,
        label: 'Préparation de la recherche',
        type: 'planning' as const,
      });
    } else if (activity.phase === 'searching') {
      steps.push({
        detail: `${activity.queryCount} recherche${activity.queryCount > 1 ? 's' : ''}`,
        domains: [],
        isError: false,
        isPending: activity.status === 'running',
        key: activity.phase,
        label: 'Recherche sur le web',
        type: 'search' as const,
      });
    } else if (activity.phase === 'reading') {
      steps.push({
        detail: `${activity.pageCount} page${activity.pageCount > 1 ? 's' : ''} consultée${activity.pageCount > 1 ? 's' : ''}`,
        domains: [],
        isError: false,
        isPending: activity.status === 'running',
        key: activity.phase,
        label: 'Consultation des sources',
        type: 'fetch' as const,
      });
    } else {
      steps.push({
        detail: null,
        domains: [],
        isError: false,
        isPending: activity.status === 'running',
        key: activity.phase,
        label: 'Rédaction de la réponse',
        type: 'synthesizing' as const,
      });
    }
  }

  return steps;
}

export function ChatWebActivity({
  activities = [],
  className,
  isStreaming = false,
  toolParts,
}: {
  activities?: WebResearchActivityDTO[];
  className?: string;
  isStreaming?: boolean;
  toolParts: WebActivityToolPart[];
}) {
  // null = aucun choix manuel : l'ouverture suit l'avancement de la recherche.
  // Dès que l'utilisateur replie/déplie lui-même, son choix prime.
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const steps = activities.length > 0
    ? toTypedActivitySteps(activities)
    : toolParts
      .map(toWebActivityStep)
      .filter((step): step is WebActivityStep => step !== null);
  const toolSteps = toolParts
    .map(toWebActivityStep)
    .filter((step): step is WebActivityStep => step !== null);
  const hasPendingStep = steps.some((step) => step.isPending);
  const hasError = steps.some((step) => step.isError) || toolSteps.some((step) => step.isError);
  const typedPageCount = [...activities].reverse()
    .find((activity) => activity.phase === 'reading')?.pageCount;
  const fetchCount = typedPageCount
    ?? steps.filter((step) => step.type === 'fetch' && !step.isError).length;

  if (steps.length === 0) {
    return null;
  }

  // Parité mobile Android/iOS : le panneau reste ouvert tant que la recherche
  // est en cours. « Terminée » = plus aucune étape active ET rédaction close ;
  // les micro-intervalles entre deux phases ne referment donc pas le panneau.
  const researchDone = !hasPendingStep
    && steps.some((step) => step.type === 'synthesizing' && !step.isPending);
  const inProgress = isStreaming && !researchDone;
  const isOpen = userOpen ?? inProgress;

  const summary = inProgress
    ? 'Recherche sur le web'
    : hasError
      ? 'Recherche web terminée avec des limites'
      : fetchCount > 0
        ? `Recherche web terminée · ${fetchCount} page${fetchCount > 1 ? 's' : ''} consultée${fetchCount > 1 ? 's' : ''}`
        : 'Recherche web terminée';

  return (
    <ChainOfThought
      className={className}
      onOpenChange={setUserOpen}
      open={isOpen}
    >
      <ChainOfThoughtHeader
        className={inProgress
          ? 'min-h-8 max-w-2xl text-[13px] [&>svg:first-child]:animate-spin'
          : 'min-h-8 max-w-2xl text-[13px]'}
        icon={inProgress ? LoaderCircleIcon : GlobeIcon}
      >
        <span className={inProgress ? 'font-medium text-foreground' : 'font-medium'}>
          {summary}
        </span>
      </ChainOfThoughtHeader>

      <ChainOfThoughtContent className="ml-1 max-w-2xl">
        {steps.map((step) => {
          const showLoader = step.isPending && isStreaming;

          return (
            <ChainOfThoughtStep
              className={cn(
                step.isError && 'text-amber-700 dark:text-amber-500',
                showLoader && '[&>div:first-child>svg]:animate-spin',
              )}
              description={step.detail}
              icon={step.isError
                ? CircleAlertIcon
                : showLoader
                  ? LoaderCircleIcon
                  : step.type === 'planning'
                    ? ListChecksIcon
                    : step.type === 'search'
                      ? SearchIcon
                      : step.type === 'synthesizing'
                        ? SparklesIcon
                        : ExternalLinkIcon}
              key={step.key}
              label={step.label}
              status={showLoader ? 'active' : 'complete'}
            >
              {step.domains.length > 0 ? (
                <ChainOfThoughtSearchResults>
                  {step.domains.map((domain) => (
                    <ChainOfThoughtSearchResult key={domain}>
                      {domain}
                    </ChainOfThoughtSearchResult>
                  ))}
                </ChainOfThoughtSearchResults>
              ) : null}
            </ChainOfThoughtStep>
          );
        })}
      </ChainOfThoughtContent>
    </ChainOfThought>
  );
}
